import { skillNodeById } from "../content/skill-tree";
import { AFFIXES, COMPONENTS } from "../content/affixes";
import { frameCargoDamageBonus, frameWeaponDamageMultiplier, SHIP_FRAMES } from "../content/ships";
import { WEAPONS } from "../content/weapons";
import type { BankedProfile } from "../simulation/persistence";
import type { WorldSnapshot } from "../simulation/snapshot";

const TRAIL_NAMES: Record<string, string> = {
  trail_ember: "Ember",
  trail_ion: "Ion",
  trail_rust: "Rust",
};

/**
 * Shared ship-upgrades / loadout readout from real sim + banked profile state.
 * Used by Carrier Bay (docked) and the mid-run pause status panel.
 */
export function buildShipStatusHtml(profile: BankedProfile, snap: WorldSnapshot): string {
  const frameId =
    snap.phase === "carrier" || snap.phase === "extracted" || snap.phase === "dead"
      ? profile.selectedFrameId
      : snap.player.frameId;
  const frame = SHIP_FRAMES[frameId] ?? SHIP_FRAMES.skimmer!;
  const weapons =
    snap.phase === "carrier" || snap.phase === "extracted" || snap.phase === "dead"
      ? profile.selectedWeapons
      : snap.runInventory.weapons;
  const affixes = snap.runInventory.affixes;
  const components = snap.runInventory.components;
  const trailId = snap.cosmeticsTrailId || profile.selectedTrailId;
  const skills = snap.runProgress.takenNodes;
  const cargoDamageBonus = frameCargoDamageBonus(frame, snap.runInventory.salvage);
  const weaponDamageMult = frameWeaponDamageMultiplier(frame, snap.runInventory.salvage);
  const weaponStatus =
    frame.weaponDamageMult || frame.cargoDamagePerSalvage
      ? ` · Dmg×${weaponDamageMult.toFixed(2)}${frame.cargoDamagePerSalvage ? ` (Raid +${Math.round(cargoDamageBonus * 100)}% / ${Math.round((frame.cargoDamageCap ?? 0) * 100)}%)` : ""}`
      : "";
  const boostStatus = frame.boostShieldRegenPerSecond
    ? ` · Boost shield +${frame.boostShieldRegenPerSecond}/s · Burn×${frame.boostFuelDrainMult ?? 1}`
    : "";
  const killStatus = frame.killBoostRestore
    ? ` · Kill boost +${Math.round(frame.killBoostRestore * 100)}%`
    : "";
  const ignitionStatus = frame.boostCycleReduction
    ? ` · Boost ignition −${Math.round(frame.boostCycleReduction * 100)}% cooldown / ${frame.boostCycleProcCooldownS ?? 0}s`
    : "";
  const shieldBreakStatus = frame.shieldBreakCycleReduction
    ? ` · Shield break −${Math.round(frame.shieldBreakCycleReduction * 100)}% cooldown / ${frame.shieldBreakCycleProcCooldownS ?? 0}s`
    : "";
  const salvageCycleStatus = frame.salvageOrdnanceCycleReduction
    ? ` · Salvage ordnance cycle −${Math.round(frame.salvageOrdnanceCycleReduction * 100)}%`
    : "";
  const enemyShieldBreakStatus = frame.enemyShieldBreakCycleReduction
    ? ` · Enemy shield break −${Math.round(frame.enemyShieldBreakCycleReduction * 100)}% cooldown`
    : "";
  const projectileRecallStatus = frame.projectileRecallCycleReduction
    ? ` · Projectile catch −${Math.round(frame.projectileRecallCycleReduction * 100)}% cooldown · +${Math.round((frame.projectileRecallBoostRestore ?? 0) * 100)}% boost`
    : "";

  const skillLines =
    skills.length === 0
      ? `<li class="muted">No skill nodes yet — level up in combat</li>`
      : skills
          .map((id) => {
            const node = skillNodeById(id);
            return node
              ? `<li><strong>${node.name}</strong> <span>${node.description}</span></li>`
              : `<li>${id}</li>`;
          })
          .join("");

  const lastRun = profile.lastRun
    ? `<p class="status-last-run">Last run (${profile.lastRun.outcome}): Lv ${profile.lastRun.level} · Score ${profile.lastRun.score.toLocaleString()} · Best streak ${profile.lastRun.bestStreak} · ${profile.lastRun.skillNodes} skills</p>`
    : "";

  const weaponLines = weapons
    .map((id) => {
      const w = WEAPONS[id];
      return w
        ? `<li><strong>${w.name}</strong> <span>${w.slot} · ${w.description}</span></li>`
        : `<li>${id}</li>`;
    })
    .join("");

  const affixLines =
    affixes.length === 0
      ? `<li class="muted">No affixes installed this run</li>`
      : affixes
          .map((id) => {
            const a = AFFIXES.find((x) => x.id === id);
            return a
              ? `<li><strong>${a.name}</strong> <span>${a.description}</span></li>`
              : `<li>${id}</li>`;
          })
          .join("");

  const componentLines =
    components.length === 0
      ? `<li class="muted">No components fitted this run</li>`
      : components
          .map((id) => {
            const c = COMPONENTS.find((x) => x.id === id);
            return c
              ? `<li><strong>${c.name}</strong> <span>${c.description}</span></li>`
              : `<li>${id}</li>`;
          })
          .join("");

  const unlockedFrames = profile.unlockedFrames.map((id) => SHIP_FRAMES[id]?.name ?? id).join(", ");
  const unlockedWeapons = profile.unlockedWeapons.map((id) => WEAPONS[id]?.name ?? id).join(", ");
  const cosmetics = profile.cosmetics.map((id) => TRAIL_NAMES[id] ?? id).join(", ");

  return `
    <div class="ship-status-block" data-frame="${frame.id}">
      <div class="status-row">
        <strong>${frame.name}</strong>
        <span>${frame.role} — ${frame.drawback}</span>
        <em>H${frame.hull} · S${frame.shield} · Spd×${frame.speedMult} · Cargo×${frame.cargoMult}${frame.recycleMult ? ` · Recycle×${frame.recycleMult}` : ""}${weaponStatus}${boostStatus}${killStatus}${ignitionStatus}${shieldBreakStatus}${salvageCycleStatus}${enemyShieldBreakStatus}${projectileRecallStatus}</em>
      </div>
      <h4>Weapons</h4>
      <ul>${weaponLines || `<li class="muted">None equipped</li>`}</ul>
      <h4>Components</h4>
      <ul>${componentLines}</ul>
      <h4>Affixes</h4>
      <ul>${affixLines}</ul>
      <h4>Skill tree</h4>
      <ul>${skillLines}</ul>
      <h4>Trail</h4>
      <p class="status-trail">${TRAIL_NAMES[trailId] ?? trailId}</p>
      ${lastRun}
      <h4>Banked unlocks</h4>
      <p class="status-banked">
        Frames: ${unlockedFrames || "—"}<br/>
        Weapons: ${unlockedWeapons || "—"}<br/>
        Cosmetics: ${cosmetics || "—"}<br/>
        Blueprints: ${profile.blueprints.length ? profile.blueprints.join(", ") : "none"} · Salvage ${profile.salvage}
      </p>
    </div>
  `;
}
